import type {
  FlowSession,
  FlowSessionSource,
  RegistrationDraft,
} from '../model/auth.types';
import { LOGIN_PATH } from '../model/auth.paths.model';
import type { AuthPath } from '../model/auth.paths.model';
import {
  RegisterFlowPath,
  RegisterStep,
  getSignupStepForPath,
  getPathForStep,
  toRegisterFlowPath,
} from '../model/registration.model';
import {
  constrainStepByProgress,
  isEmailValid,
  type RegisterStepValidationRules,
} from './register.progress';
import type { RegisterValues } from './register.types';

export type InitialRegisterState = {
  step: RegisterStep;
  values: RegisterValues;
  sessionSource: FlowSessionSource;
  redirectPath?: string;
};

export type RequestedRouteState = {
  source: FlowSessionSource;
  requestedStep: RegisterStep;
};

export type RegisterPathSyncInput = {
  pathname: string;
  currentStep: RegisterStep;
  currentSource: FlowSessionSource;
  values: RegisterValues;
  validationRules: RegisterStepValidationRules;
};

export type RegisterPathSyncResult = {
  nextStep: RegisterStep;
  nextSource: FlowSessionSource;
  shouldClearCode: boolean;
  redirectPath?: AuthPath;
};

export type ResolveInitialRegisterStateInput = {
  initialDraft: RegistrationDraft;
  validationRules: RegisterStepValidationRules;
  persistedSession: FlowSession | null;
  clearPersistedSession: () => void;
};

const LOGIN_ROUTE = LOGIN_PATH;
const LOGIN_CONFIRMATION_CODE_ROUTE = getPathForStep('code', 'signin');
const SIGNUP_EMAIL_ROUTE = getPathForStep('email', 'signup');

const isWindowAvailable = (): boolean => {
  return typeof window !== 'undefined';
};

const readCurrentPathname = (): string => {
  if (!isWindowAvailable()) {
    return SIGNUP_EMAIL_ROUTE;
  }

  return window.location.pathname;
};

const isConfirmationCodePath = (pathname: RegisterFlowPath | null): boolean => {
  return (
    pathname === LOGIN_CONFIRMATION_CODE_ROUTE ||
    pathname === getPathForStep('code', 'signup')
  );
};

const getNavigationEntryPathname = (
  entry: PerformanceNavigationTiming,
): string | null => {
  if (!entry.name) {
    return null;
  }

  try {
    return new URL(entry.name, window.location.origin).pathname;
  } catch {
    return null;
  }
};

const isReloadNavigation = (pathname: string): boolean => {
  if (!isWindowAvailable()) {
    return false;
  }

  const navigationEntries =
    window.performance?.getEntriesByType?.('navigation') ?? [];
  const firstNavigationEntry = navigationEntries[0] as
    | PerformanceNavigationTiming
    | undefined;

  if (firstNavigationEntry?.type) {
    if (firstNavigationEntry.type !== 'reload') {
      return false;
    }

    const navigationEntryPathname =
      getNavigationEntryPathname(firstNavigationEntry);
    if (navigationEntryPathname === null) {
      return true;
    }

    return navigationEntryPathname === pathname;
  }

  const legacyNavigationType = window.performance?.navigation?.type;
  return legacyNavigationType === 1;
};

export const getRequestedRouteStateFromPath = (
  pathname: RegisterFlowPath | null,
  persistedSource: FlowSessionSource | null,
): RequestedRouteState => {
  if (pathname === LOGIN_CONFIRMATION_CODE_ROUTE) {
    return {
      source: 'signin',
      requestedStep: 'code',
    };
  }

  const requestedSignupStep = pathname ? getSignupStepForPath(pathname) : null;
  if (requestedSignupStep) {
    return {
      source: 'signup',
      requestedStep: requestedSignupStep,
    };
  }

  return {
    source: persistedSource === 'signin' ? 'signin' : 'signup',
    requestedStep: persistedSource === 'signin' ? 'code' : 'email',
  };
};

export const resolveRegisterPathSync = ({
  pathname,
  currentStep,
  currentSource,
  values,
  validationRules,
}: RegisterPathSyncInput): RegisterPathSyncResult | null => {
  const currentFlowPath = toRegisterFlowPath(pathname);
  if (!currentFlowPath) {
    return null;
  }

  const requestedRouteState = getRequestedRouteStateFromPath(
    currentFlowPath,
    currentSource,
  );
  const shouldClearCode =
    currentStep === 'code' && requestedRouteState.requestedStep !== 'code';

  if (requestedRouteState.source === 'signin') {
    if (!isEmailValid(values.email, validationRules)) {
      return {
        nextStep: 'email',
        nextSource: 'signin',
        shouldClearCode,
        redirectPath: LOGIN_ROUTE,
      };
    }

    const signInCodePath = getPathForStep('code', 'signin');
    return {
      nextStep: 'code',
      nextSource: 'signin',
      shouldClearCode,
      redirectPath:
        currentFlowPath === signInCodePath ? undefined : signInCodePath,
    };
  }

  const constrainedStep = constrainStepByProgress(
    requestedRouteState.requestedStep,
    values,
    validationRules,
  );
  const signupPath = getPathForStep(constrainedStep, 'signup');

  return {
    nextStep: constrainedStep,
    nextSource: 'signup',
    shouldClearCode,
    redirectPath: currentFlowPath === signupPath ? undefined : signupPath,
  };
};

const getInitialValues = (initialDraft: RegistrationDraft): RegisterValues => {
  return {
    ...initialDraft,
    code: '',
  };
};

export const resolveInitialRegisterState = ({
  initialDraft,
  validationRules,
  persistedSession,
  clearPersistedSession,
}: ResolveInitialRegisterStateInput): InitialRegisterState => {
  const values: RegisterValues = {
    ...getInitialValues(initialDraft),
  };

  if (
    persistedSession?.source === 'signin' &&
    isEmailValid(persistedSession.email, validationRules)
  ) {
    const email = validationRules.parseEmail(persistedSession.email);
    if (email) {
      values.email = email;
    }
  }

  const currentPathname = readCurrentPathname();
  const currentFlowPath = toRegisterFlowPath(currentPathname);

  if (
    isReloadNavigation(currentPathname) &&
    isConfirmationCodePath(currentFlowPath)
  ) {
    clearPersistedSession();

    const isSignInConfirmation =
      currentFlowPath === LOGIN_CONFIRMATION_CODE_ROUTE;

    return {
      step: 'email',
      values,
      sessionSource: isSignInConfirmation ? 'signin' : 'signup',
      redirectPath: isSignInConfirmation ? LOGIN_ROUTE : SIGNUP_EMAIL_ROUTE,
    };
  }

  const requestedRouteState = getRequestedRouteStateFromPath(
    currentFlowPath,
    persistedSession?.source ?? null,
  );

  if (requestedRouteState.source === 'signin') {
    if (!isEmailValid(values.email, validationRules)) {
      return {
        step: 'email',
        values,
        sessionSource: 'signin',
        redirectPath: currentPathname === LOGIN_ROUTE ? undefined : LOGIN_ROUTE,
      };
    }

    const signinCodePath = getPathForStep('code', 'signin');

    return {
      step: 'code',
      values,
      sessionSource: 'signin',
      redirectPath:
        currentPathname === signinCodePath ? undefined : signinCodePath,
    };
  }

  const constrainedStep = constrainStepByProgress(
    requestedRouteState.requestedStep,
    values,
    validationRules,
  );
  const signupPathForStep = getPathForStep(constrainedStep, 'signup');

  return {
    step: constrainedStep,
    values,
    sessionSource: 'signup',
    redirectPath:
      currentPathname === signupPathForStep ? undefined : signupPathForStep,
  };
};
